import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { CreatorPermission, Shift, TeamMemberRole } from '@api'
import { format } from 'date-fns/format'
import { useUnit } from 'effector-react'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Page } from '@/components/layout/page/page'
import { Button } from '@/components/ui/button'
import { $account } from '@/models/account.ts'
import { $creators, loadCreatorsFx } from '@/models/creators.ts'
import type { Creator } from '@/modules/creators/types'
import { getAvailableCreators } from '@/modules/shifts/data/service'
import { useGetFilteredShiftsEntities } from '@/modules/shifts/hooks/use-get-filtered-shifts-entities'
import { useShiftMutations } from '@/modules/shifts/hooks/use-shift-mutations'
import { CreateShiftDialog } from '@/modules/shifts/ui/dialogs/create-shift'
import { EditShiftDialog } from '@/modules/shifts/ui/dialogs/edit-shift'
import { ShiftsFilters } from '@/modules/shifts/ui/filters'
import { getFirstDayOfWeek, getRepetitionDays } from '@/modules/shifts/utils'
import type { ShiftsCalendarRef } from '@/modules/shifts/views/calendar'
import { ShiftsCalendar } from '@/modules/shifts/views/calendar'
import { useFetchTeamTable } from '@/modules/team/hooks/use-fetch-team-table'
import { AppRoutes } from '@/utils/constants'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'
import { hasPermission } from '@/utils/helpers'

const getMonthsRange = (date: Date) => {
  const year = date.getFullYear()
  const month = date.getMonth()
  const from = new Date(year, month - 1, 1)
  const to = new Date(year, month + 2, 0)

  return { from, to }
}

const hasCreatorPermission = (creator: Creator) =>
  hasPermission(creator, undefined, CreatorPermission.ACCESS_ONLYFANS_PROFILE)

const hasModifyPermission = (creator: Creator) =>
  hasPermission(creator, undefined, CreatorPermission.MODIFY_SHIFT)

export const ShiftsView = () => {
  const [searchParams] = useSearchParams()
  const time = searchParams.get('time')
  const date = time ? new Date(+time) : new Date()
  const [showingDate, setShowingDate] = useState(date)

  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [editingShift, setEditingShift] = useState<Shift | null>(null)
  const [dateToShow, setDateToShow] = useState<Date | undefined>()
  const calendarRef = useRef<ShiftsCalendarRef | null>(null)
  const account = useUnit($account)
  const allCreators = useUnit($creators)
  const isFetchingCreators = useUnit(loadCreatorsFx.pending)
  const { deleteShift } = useShiftMutations()
  const navigate = useNavigate()

  const isAdminOrLeader = [TeamMemberRole.ADMIN, TeamMemberRole.LEADER].some(
    (role) => role === account?.memberRole,
  )

  const { data: employees, isFetching: isFetchingEmployees } =
    useFetchTeamTable({ enabled: isAdminOrLeader })

  const { data: availableCreators, isFetching: isAvailableCreatorsFetching } =
    useQuery(
      'availableCreators',
      () => getAvailableCreators(account!.memberId!),
      { enabled: !!account?.memberId && !isAdminOrLeader },
    )

  const creators = useMemo(() => {
    return (
      isAdminOrLeader
        ? allCreators
        : allCreators.filter(
            (creator) =>
              hasCreatorPermission(creator) ||
              availableCreators?.ids?.includes(creator.id),
          )
    ).filter(({ licenseId }) => licenseId)
  }, [allCreators, availableCreators, isAdminOrLeader])

  const creatorsWithPermissions = useMemo(
    () => creators.filter(hasModifyPermission),
    [creators],
  )

  const canCreateShift = !!creatorsWithPermissions.length

  const isShiftCanBeModified = useCallback(
    (shift: Shift) => {
      const creator = creators.find((creator) => creator.id === shift.creatorId)

      return creator ? hasModifyPermission(creator) : false
    },
    [creators],
  )

  const isFiltersDataLoading =
    isFetchingCreators ||
    (!isAdminOrLeader && isAvailableCreatorsFetching) ||
    isFetchingEmployees

  const {
    data,
    filteredCreators,
    isLoading,
    filters,
    setFilters,
    filterShiftByPeople,
  } = useGetFilteredShiftsEntities(
    'shifts',
    getMonthsRange(showingDate),
    creators,
    employees,
    isFiltersDataLoading,
  )

  useEffect(() => {
    if (Object.values(data || {}).length) {
      trackFeatureUsage(TrackedFeatures.Shifts)
    }
  }, [data])

  useEffect(() => {
    setFilters((prev) => ({ ...prev, ...getMonthsRange(showingDate) }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showingDate.getMonth()])

  const handleSetShowingDate = useCallback(
    (date: Date) => {
      setShowingDate(date)
      navigate(`${AppRoutes.Shifts.List.asUrl()}?time=${date.getTime()}`, {
        replace: true,
      })
    },
    [navigate],
  )

  const navigateToShiftsHistory = () => {
    navigate(AppRoutes.Shifts.History.asUrl())
  }

  const handleChangeDateToShow = (date?: Date) => {
    if (date) {
      setDateToShow(date)
      calendarRef.current?.jumpToWeek(date)
      setTimeout(() => {
        calendarRef.current?.pulseDay(date)
      }, 500)
    }
  }

  const handleClickJumpToThisWeek = () => {
    setDateToShow(undefined)
    calendarRef.current?.jumpToWeek(new Date())
  }

  const areActionsDisabled = !creators.length

  const disableJumpToThisWeek = useMemo(() => {
    return (
      getFirstDayOfWeek(new Date()).getTime() ===
      getFirstDayOfWeek(showingDate).getTime()
    )
  }, [showingDate])

  return (
    <Page
      title="Shifts"
      headerOptions={
        <div className="flex space-x-4">
          {canCreateShift && (
            <Button
              onClick={navigateToShiftsHistory}
              variant="outline"
              disabled={areActionsDisabled}
            >
              Shift History
            </Button>
          )}
          {canCreateShift && (
            <Button
              onClick={() => setIsCreateModalVisible(true)}
              className="flex gap-1"
              disabled={areActionsDisabled}
            >
              <PlusIcon
                width={25}
                height={25}
              />
              Create Shift
            </Button>
          )}
        </div>
      }
    >
      <div className="mb-6 flex flex-1 flex-col items-center justify-between gap-6 sm:flex-row">
        <ShiftsFilters
          onChange={setFilters}
          values={filters}
          includeDatePicker
          includeTeamMembers={isAdminOrLeader}
          onChangeDatePicker={handleChangeDateToShow}
          datePickerValue={dateToShow}
          creators={creators}
          teamMembers={employees}
        />
        <div className="flex items-center gap-4 self-end sm:self-center">
          <Button
            variant="ghost"
            className="w-fit p-0"
            disabled={disableJumpToThisWeek}
            onClick={handleClickJumpToThisWeek}
          >
            Jump to this week
          </Button>
          <span className="text-lg font-semibold">
            {format(showingDate, 'MMMM, yyyy')}
          </span>
        </div>
      </div>
      <ShiftsCalendar
        ref={calendarRef}
        allCreators={creators}
        filteredCreators={filteredCreators}
        shiftGroupsMapByCreators={data}
        isLoading={isLoading}
        onClickEdit={setEditingShift}
        onClickDelete={deleteShift}
        areActionsVisible={isShiftCanBeModified}
        filterShiftByPeople={filterShiftByPeople}
        setMonth={handleSetShowingDate}
        date={showingDate}
      />
      <CreateShiftDialog
        isModalVisible={canCreateShift && isCreateModalVisible}
        onClose={() => setIsCreateModalVisible(false)}
        creators={creatorsWithPermissions}
      />
      {editingShift && data && (
        <EditShiftDialog
          isModalVisible={isShiftCanBeModified(editingShift) && !!editingShift}
          onClose={() => setEditingShift(null)}
          shift={editingShift}
          creators={creatorsWithPermissions}
          repeatDays={getRepetitionDays(
            data[editingShift.creatorId],
            editingShift.repeatableShiftId,
          )}
        />
      )}
    </Page>
  )
}
